import { useState, useEffect, } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled, alpha } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { Button, Menu, Typography, Avatar, Tooltip, Divider, Box, Badge, MenuItem, CircularProgress, useMediaQuery } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useSelector } from '../../../redux/store';
import Iconify from '../../../reusable-components/iconify';
import { getAllCartsItemsCount } from '../../../redux/slices/orderSheet';
import axiosInstance from '../../../utils/axios';

export default function OrderRentalGlobalCart() {
  const [carts, setCarts] = useState([]);
  const navigate = useNavigate();
  const { data: { allCartsItemsCount } } = useSelector((state) => state.orderSheet);
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const handleClose = () => {
    setCarts([]);
    setAnchorEl(null);
  };

  const goToCart = (cart) => {
    handleClose();
    navigate(cart.purchasing
      ? `/dashboard/orders/new-order/cart/${cart.facilityData.value}`
      : `/dashboard/rentals/rental-orders/rental-cart/${cart.facilityData.value}`);
  }

  const fetchCartFacilities = () => {
    setLoading(true);
    axiosInstance.get(`/FacilitiesWithCarts`)
      .then(res => {
        setCarts(res.data.length > 0 ?
          Object.values(res.data.reduce((acc, cart) => {
            const facilityId = cart.facilityData.value;
            if (!acc[facilityId]) {
              acc[facilityId] = {
                label: cart.facilityData.label,
                carts: [],
              };
            }
            acc[facilityId].carts.push(cart);
            return acc;
          }, {}))
          : [{ label: 'No Carts', carts: [] }]);
      })
      .catch(() => enqueueSnackbar("Failed to load carts", { variant: "error" }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (open) fetchCartFacilities(); }, [open]);
  useEffect(() => { dispatch(getAllCartsItemsCount()); }, []);

  return <>
    <Button
      id="nav-cart-button"
      aria-controls={open ? 'nav-cart-menu' : undefined}
      aria-haspopup="true"
      aria-expanded={open ? 'true' : undefined}
      disableElevation
      onClick={(e) => setAnchorEl(e.currentTarget)}
      size="small"
      sx={{minWidth: 0}}
    >
      {allCartsItemsCount > 0 ? <Badge
        badgeContent={allCartsItemsCount <= 99 ? allCartsItemsCount : "99+"}
        color="info"
        anchorOrigin={{ horizontal: "left", vertical: "top" }}
        sx={{ '& .MuiBadge-badge': { fontSize: '10px' }, ml: 0.5 }}
      >
        <Iconify icon="mdi:shopping-cart-outline" width={24} height={24} />
      </Badge> : <Iconify icon="mdi:shopping-cart-outline" width={24} height={24} />}
      {!isMobile && <Iconify icon="mdi:chevron-down" width={24} height={24} />}
    </Button>

    <StyledMenu id="nav-cart-menu" MenuListProps={{ 'aria-labelledby': 'nav-cart-button' }} anchorEl={anchorEl} open={open} onClose={handleClose}>
      {loading && <MenuItem>Loading... <CircularProgress sx={{ ml: 1 }} size={20} /></MenuItem>}
      {carts.map((group, index) => (
        <div key={index}>
          <Box onClick={() => { if (group.carts.length === 1) goToCart(group.carts[0]) }}
            sx={{ py: 0.3, display: "flex", alignItems: "center", justifyContent: "space-between", ":hover": group.carts.length === 1 ? { backgroundColor: "rgba(22, 28, 36, 0.08)", cursor: "pointer" } : {} }}>
            <Typography variant="subtitle1" mx={2}>
              {group.label}
            </Typography>
            <Box mr={2}>
              {group.carts.map((cart, cartIndex) => (
                <Button key={cartIndex} onClick={() => goToCart(cart)} color="primary" style={{ minWidth: '0', padding: '.5', marginLeft: '4px' }}>
                  <Tooltip title={cart.purchasing ? 'Purchasing' : 'Rentals'} arrow>
                    <Avatar
                      sx={{
                        height: 30,
                        width: 30,
                        backgroundColor: '#fff',
                        border: `1px solid ${cart.purchasing ? '#b128bc' : '#4ca5e7'}`,
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      <Iconify
                        icon={cart.purchasing ? 'clarity:store-line' : 'carbon:hospital-bed'}
                        height={20}
                        width={20}
                        style={{ color: cart.purchasing ? '#b128bc' : '#4ca5e7' }}
                      />
                    </Avatar>
                  </Tooltip>
                </Button>
              ))}
            </Box>
          </Box>
          {index < carts.length - 1 && <Divider variant="fullWidth" />}
        </div>
      ))}
    </StyledMenu>
  </>;
}

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'left',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
    sx={{
      ml: 10,
      mt: 0.5,
    }}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
    backgroundColor: '#fff',
    overflow: 'auto',
    border: 'solid 1px #c1c9d0',
    minWidth: 200,
    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
      borderBottom: '0.5px solid #637381',
      '&:last-child': {
        borderBottom: 'none',
      },
    },
  },
}));
